import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ResponsiveDialog from '../common/ResponsiveDialog';
import inquiryApi from '../../services/inquiryApi';
import type { PrLink, SapPoView } from '../../types/inquiry';
import { errorText, formatDate, formatDateTime } from '../../utils/e2i';
import { DocLinks, money, quantity, statusColor } from './inquiryCommon';

/**
 * A SAP purchase order, read-only (PR and PO Inquiry): read live from SAP (or PRPO's synced copy when SAP does not
 * answer), with its PRs, its lines (received and open quantities) and its goods receipts, each GRPO with its lines
 * and the goods issue that followed it.
 */
interface Props {
  open: boolean;
  companyId: number;
  companyCurrency: string;
  docEntry: number | null;
  onClose: () => void;
  onOpenPr: (pr: PrLink) => void;
  onOpenPrpoPo: (poId: number) => void;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" component="div" sx={{ wordBreak: 'break-word' }}>{children || '—'}</Typography>
  </Box>
);

const SapPODialog: React.FC<Props> = ({ open, companyId, companyCurrency, docEntry, onClose, onOpenPr, onOpenPrpoPo }) => {
  const [po, setPo] = useState<SapPoView | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !docEntry || !companyId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPo(null);
    inquiryApi.sapPo(companyId, docEntry)
      .then((v) => { if (!cancelled) setPo(v); })
      .catch((e: any) => { if (!cancelled) setError(errorText(e, 'The purchase order could not be opened')); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, companyId, docEntry]);

  const amount = (n: number | null, currency?: string) => money(n, currency ?? po?.currency ?? '', companyCurrency, po?.amountDecimals ?? 2);
  const named = (kind: 'projects' | 'divisions' | 'departments', code: string) => (code && po?.names[kind]?.[code] ? `${code} ${po.names[kind][code]}` : code);
  const prLinks = (prs: PrLink[]) => (
    <DocLinks
      items={prs}
      label={(p) => p.number}
      ariaLabel={(p) => `Open purchase requisition ${p.number}`}
      onOpen={(p) => (p.id ? onOpenPr(p) : undefined)}
      max={5}
    />
  );

  return (
    <ResponsiveDialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="sap-po-title">
      <DialogTitle id="sap-po-title" sx={{ pr: 6 }}>
        SAP purchase order {po?.docNum ?? ''}
        {po && <Chip size="small" sx={{ ml: 1 }} color={statusColor(po.status)} label={po.status} />}
        <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Loading the purchase order" /></Box>}
        {error && <Alert severity="error">{error}</Alert>}
        {po && (
          <Stack spacing={2}>
            <Typography variant="caption" color="text.secondary">
              {po.source === 'live' ? `Read from SAP ${formatDateTime(po.asOf)}` : `PRPO's synced copy of ${formatDateTime(po.asOf)}`}
            </Typography>
            {po.note && <Alert severity="warning">{po.note}</Alert>}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}><Field label="Vendor">{`${po.vendorCode} ${po.vendorName}`.trim()}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="PO date">{formatDate(po.date)}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Delivery date">{formatDate(po.dueDate)}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Document date">{formatDate(po.taxDate)}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Vendor ref.">{po.vendorRef}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Total">{amount(po.total)}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="VAT">{amount(po.vat)}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Payment terms">{po.paymentTerms}</Field></Grid>
              <Grid item xs={6} sm={3} md={2}><Field label="Project">{named('projects', po.project)}</Field></Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Field label="Purchase requisitions">{po.prs.length ? prLinks(po.prs) : ''}</Field>
              </Grid>
              {po.prpoPo && (
                <Grid item xs={12} sm={6} md={4}>
                  <Field label="Created in PRPO as">
                    <Link component="button" type="button" variant="body2" onClick={() => onOpenPrpoPo(po.prpoPo!.id)}>{po.prpoPo.number}</Link>
                  </Field>
                </Grid>
              )}
              {po.comments && <Grid item xs={12}><Field label="Remarks">{po.comments}</Field></Grid>}
            </Grid>

            <Typography variant="subtitle1" component="h3" fontWeight={600}>Lines</Typography>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" aria-label="Purchase order lines">
                <TableHead>
                  <TableRow>
                    {['#', 'Item', 'Description', 'Quantity', 'Received', 'Open', 'Price', 'Total', 'Project', 'Division', 'Department', 'Warehouse', 'Delivery', 'Based on']
                      .map((h) => <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align={['Quantity', 'Received', 'Open', 'Price', 'Total'].includes(h) ? 'right' : undefined}>{h}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {po.lines.map((l) => (
                    <TableRow key={l.lineNum}>
                      <TableCell>{l.lineNum}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.itemCode}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>{l.description}{l.freeText ? <Typography variant="caption" color="text.secondary" display="block">{l.freeText}</Typography> : null}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{quantity(l.quantity)} {l.uom}</TableCell>
                      <TableCell align="right">{quantity(l.receivedQuantity)}</TableCell>
                      <TableCell align="right">{quantity(l.openQuantity)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{money(l.price, '', '', po.amountDecimals)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{amount(l.total, l.currency || po.currency)}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>{named('projects', l.project)}</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>{named('divisions', l.division)}</TableCell>
                      <TableCell sx={{ minWidth: 100 }}>{named('departments', l.department)}</TableCell>
                      <TableCell>{l.warehouse}</TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(l.deliveryDate)}</TableCell>
                      <TableCell sx={{ minWidth: 140 }}>
                        {l.base ? (
                          <>
                            {l.base.type}{l.base.docNum ? ` ${l.base.docNum}` : ''}
                            {l.base.pr && (
                              <Box>
                                {l.base.pr.id
                                  ? <Link component="button" type="button" variant="body2" onClick={() => onOpenPr(l.base!.pr!)}>{l.base.pr.number}</Link>
                                  : l.base.pr.number}
                              </Box>
                            )}
                          </>
                        ) : ''}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box>
              <Typography variant="subtitle1" component="h3" fontWeight={600}>
                Goods receipts ({po.receipts.documents.length})
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                SAP's goods receipt POs based on this order{po.receipts.asOf ? `, as synced ${formatDateTime(po.receipts.asOf)}` : ''}.
                Receipts posted from PRPO's GRPO page show at once.
              </Typography>
              {!po.receipts.documents.length && <Alert severity="info">No goods receipt yet.</Alert>}
              {po.receipts.documents.map((g) => (
                <Accordion key={g.docEntry} disableGutters variant="outlined">
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`grpo-${g.docEntry}`} id={`grpo-${g.docEntry}-header`}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>GRPO {g.docNum ?? g.docEntry}</Typography>
                      <Typography variant="body2" color="text.secondary">{formatDate(g.date)}</Typography>
                      <Chip size="small" color={statusColor(g.status)} label={g.status} />
                      {g.total !== null && <Typography variant="body2">{amount(g.total, g.currency || po.currency)}</Typography>}
                      {g.goodsIssue && <Chip size="small" variant="outlined" label={`Goods issue ${g.goodsIssue.docNum}`} />}
                      {g.postedInPrpo && <Chip size="small" variant="outlined" label="Posted from PRPO" />}
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails id={`grpo-${g.docEntry}`}>
                    {(g.vendorRef || g.comments) && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {g.vendorRef ? `Vendor ref. ${g.vendorRef}` : ''}{g.vendorRef && g.comments ? ' · ' : ''}{g.comments}
                      </Typography>
                    )}
                    <TableContainer sx={{ overflowX: 'auto' }}>
                      <Table size="small" aria-label={`Lines of goods receipt ${g.docNum ?? g.docEntry}`}>
                        <TableHead>
                          <TableRow>
                            {['PO line', 'Item', 'Description', 'Quantity', 'Total', 'Warehouse', 'Project'].map((h) => (
                              <TableCell key={h} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }} align={h === 'Quantity' || h === 'Total' ? 'right' : undefined}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {g.lines.map((l) => (
                            <TableRow key={l.lineNum}>
                              <TableCell>{l.poLine ?? ''}</TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.itemCode}</TableCell>
                              <TableCell sx={{ minWidth: 200 }}>{l.description}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{quantity(l.quantity)} {l.uom}</TableCell>
                              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{amount(l.total, g.currency || po.currency)}</TableCell>
                              <TableCell>{l.warehouse}</TableCell>
                              <TableCell>{named('projects', l.project)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </ResponsiveDialog>
  );
};

export default SapPODialog;
